import {
  Pixel,
  ScriptEvent,
  SessionEvent,
} from '@propps-au/pixel-analytics-types'
import { PixelFrontendClient } from '@propps-au/pixel-client'
import { Subject } from 'rxjs'
import fetch from 'unfetch'
import { logger } from '../../util/logger'
import { EmbeddedFrameCommand } from '../frame'
import { addSearchParams } from './add-search-params'
import { render } from './renderer'

export function useMountReferredTrigger(
  { fetchReferrerPath } = { fetchReferrerPath: fetchReferrerPath_ }
) {
  return async function mountReferredTrigger({
    source,
    populate,
    container,
    target,
    openOnMount,
    customMetadata = null,
    defaultButtonHost,
    framesChannel,
    pixel,
    location = window.location,
    overrideLabel,
  }: {
    source: { appId: string; foreignId: string }
    populate: any
    container?: HTMLElement | null
    target?: HTMLElement | null
    openOnMount?: boolean
    customMetadata?: string | null
    defaultButtonHost: HTMLElement
    framesChannel: Subject<EmbeddedFrameCommand>
    pixel: PixelFrontendClient
    location?: Readonly<Location>
    overrideLabel?: string
  }) {
    await pixel.initialise()
    const sessionId = await pixel.getSessionId()
    await pixel.setCustomMetadata(customMetadata)

    const framesPath = await fetchReferrerPath({
      appId: source.appId,
      listingId: source.foreignId,
      populate,
    })

    const openFrame = () => {
      const path = addSearchParams(framesPath, {
        sessionId,
        ...(customMetadata ? { customMetadata } : {}),
      })
      framesChannel.next({
        type: 'navigate',
        path,
      })
      framesChannel.next({
        type: 'show',
      })
    }

    const onClick = () => {
      pixel.dispatch<Pixel.ScriptEvent.ButtonClicked>({
        type: ScriptEvent.BUTTON_CLICKED,
        mountType: 'referred',
        appId: source.appId,
        foreignId: source.foreignId,
      })

      openFrame()
    }

    // Render the button
    const dispose = render({
      target,
      container,
      onClick,
      defaultButtonHost,
      label: overrideLabel,
    })

    // Emit mounted events
    pixel.dispatch<Pixel.ScriptEvent.ListingPageVisited>({
      type: ScriptEvent.LISTING_PAGE_VISITED,
      appId: source.appId,
      foreignId: source.foreignId,
      listingId: '',
      url: location.href,
    })

    framesChannel.next({
      type: 'trigger-mounted',
      sessionId,
      appId: source.appId,
      foreignId: source.foreignId,
      listingId: '',
    })

    // Forward session metadata
    framesChannel.next({
      type: 'set-session-metadata',
      customMetadata: customMetadata,
      search: location.search,
      referrer: {
        host: location.host,
        href: location.href,
        path: location.pathname,
      },
    })

    // Automatic open
    if (openOnMount) {
      openFrame()
    }

    // Attribution
    const search = new URLSearchParams(location.search)

    if (search.has('propps_atr_id')) {
      pixel.dispatch<Pixel.SessionEvent.SessionAttribution>({
        type: SessionEvent.SESSION_ATTRIBUTION,
        sessionId,
        // FIXME
        // @ts-ignore
        listingId: '',
        attributionId: search.get('propps_atr_id')!,
      })
    }

    return { unmount: dispose }
  }
}

const REFERRER_API_ENDPOINT = import.meta.env.VITE_REFERRER_API_ENDPOINT!
async function fetchReferrerPath_({
  appId,
  listingId,
  populate,
}: {
  appId: string
  listingId: string
  populate: any
}) {
  try {
    const res = await fetch(REFERRER_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Propps-App-Id': appId!,
      },
      body: JSON.stringify({
        listingId,
        ...populate,
      }),
    })
    if (res.ok) {
      const data = await res.json()
      logger.log('Referrer API returned data:', data)
      const { pathname, search } = new URL(data.url)
      return `${pathname}${search}`
    }
    throw Object.assign(new Error(res.statusText), { response: res })
  } catch (e) {
    logger.error('Refferer API fetch failed', e)
    throw e
  }
}
